import { Reducer } from 'react';
import { GetViewIdentifier } from '../components/reporting/GroupAccessOwnersForProfile';
import { AudienceSourceType, DistributionType, IValidationCollection } from '../models';
import { IReportingProfileConfig } from '../models/IReportingProfileConfig';
import {
	getReportingProfileConfigs,
	patchReportingProfileConfigUserCount,
	saveReportingProfileConfig,
} from '../services/ReportingUsersAPI';
import { getAudienceSource } from '../services/SurveyAPI';
import { DistributionHelper } from '../utilities/DistributionHelper';
import { CanUserAccessFeature } from '../utilities/FeatureFlags';
import { IApplicationState } from './';
import * as Actions from './Actions';
import { ActionTypes } from './ActionTypes';
import { GroupAccessOwnerViewActions, GroupAccessOwnerViewActionTypes, IsAGroupAccessOwnerViewAction } from './GroupAccessOwners/ViewStore';

export interface IReportingProfileState {
	editConfig?: IReportingProfileConfig;
	hrisId?: string;
	reportingProfileConfigs: IReportingProfileConfig[];
	fetchingGroupAccessOwners: boolean | 'N/A';
	saving: boolean;
	showReportingProfile: boolean;
	validationResults?: IValidationCollection;
	outOfSyncReportingProfileConfigIds: number[];
}

export const defaultState: IReportingProfileState = {
	reportingProfileConfigs: [],
	fetchingGroupAccessOwners: false,
	saving: false,
	showReportingProfile: false,
	outOfSyncReportingProfileConfigIds: [],
};

export const ActionCreators = {
	InitializeState: (surveyId: number, distributionType: DistributionType) => async (dispatch: any, getState: () => IApplicationState) => {
		const audienceSource = await getAudienceSource(surveyId);
		const showReportingProfile =
			CanUserAccessFeature(getState().oidc.user, 'EnableReportingProfiles') &&
			audienceSource.audienceSourceType === AudienceSourceType.Hris &&
			DistributionHelper.isNotAnonymous(distributionType);
		if (showReportingProfile) {
			const configs = await getReportingProfileConfigs(surveyId);
			await dispatch({
				type: ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_INITIALIZE_SUCCEEDED,
				payload: { showReportingProfile, reportingProfileConfigs: configs, hrisId: audienceSource.externalHrisId },
			});
		} else {
			await dispatch({
				type: ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_INITIALIZE_SUCCEEDED,
				payload: { showReportingProfile, reportingProfileConfigs: [] },
			});
		}
	},
	OpenEditConfigModal: (config: IReportingProfileConfig) => ({
		type: ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_EDIT_MODAL_OPEN,
		payload: config,
	}),
	CloseEditConfigModal: () => ({ type: ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_EDIT_MODAL_CLOSE }),
	SaveConfig: (surveyId: number, config: IReportingProfileConfig, closeModal: boolean) => async (dispatch: any) => {
		dispatch({ type: ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_CONFIG_SAVE_REQUESTED });
		dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: '' });
		const resultsModel = await saveReportingProfileConfig(surveyId, config);

		if (resultsModel.isValid) {
			dispatch({
				type: ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_CONFIG_SAVE_SUCCEEDED,
				payload: { closeModal, config: resultsModel.subject },
			});
			dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
		} else {
			dispatch({ type: ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_CONFIG_SAVE_FAILED, payload: { validationModel: resultsModel } });
			dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
		}
	},
	UpdateOutOfSyncReportingProfileConfigUserCount:
		(surveyId: number, reportingProfileConfigId: number, userCount: number) => async (dispatch: any) => {
			await patchReportingProfileConfigUserCount(surveyId, reportingProfileConfigId, userCount);
			dispatch({
				type: ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_CONFIG_UPDATE_USER_COUNT_SUCCEEDED,
				payload: { reportingProfileConfigId },
			});
		},
};

export type KnownActions =
	| Actions.IReportingAccessProfileInitializeSucceeded
	| Actions.IReportingAccessProfileEditModalOpened
	| Actions.IReportingAccessProfileEditModalClosed
	| Actions.IReportingAccessProfileConfigSaveRequested
	| Actions.IReportingAccessProfileConfigSaveSucceeded
	| Actions.IReportingAccessProfileConfigSaveFailed
	| Actions.IReportingAccessProfileConfigUpdateUserCountSucceeded
	| GroupAccessOwnerViewActions;

export const reducer: Reducer<IReportingProfileState, KnownActions> = (
	state: IReportingProfileState | undefined,
	action: KnownActions,
): IReportingProfileState => {
	if (!state) {
		return defaultState;
	}

	switch (action.type) {
		case ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_INITIALIZE_SUCCEEDED:
			return {
				...state,
				...action.payload,
			};
		case ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_EDIT_MODAL_OPEN:
			return {
				...state,
				editConfig: action.payload,
			};
		case ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_EDIT_MODAL_CLOSE:
			return {
				...state,
				editConfig: undefined,
				validationResults: undefined,
			};
		case ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_CONFIG_SAVE_REQUESTED:
			return {
				...state,
				saving: true,
			};
		case ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_CONFIG_SAVE_SUCCEEDED: {
			const configs = state.reportingProfileConfigs.map((c) => {
				if (c.reportingProfileConfigId === action.payload.config.reportingProfileConfigId) {
					return action.payload.config;
				}
				return c;
			});
			return {
				...state,
				reportingProfileConfigs: configs,
				editConfig: action.payload.closeModal ? undefined : action.payload.config,
				saving: false,
				validationResults: undefined,
			};
		}
		case ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_CONFIG_SAVE_FAILED:
			return {
				...state,
				editConfig: action.payload.validationModel.subject,
				saving: false,
				validationResults: action.payload.validationModel.validationResults,
			};
		case ActionTypes.REPORTING_ACCESS_PROFILES_SECTION_CONFIG_UPDATE_USER_COUNT_SUCCEEDED:
			return {
				...state,
				// remove the id of the updated config from the list.
				outOfSyncReportingProfileConfigIds: state.outOfSyncReportingProfileConfigIds.filter(
					(id) => id !== action.payload.reportingProfileConfigId,
				),
			};
		default: {
			if (IsAGroupAccessOwnerViewAction(action)) {
				return HandleGroupAccessOwnerActions(state, action);
			}

			return state;
		}
	}
};

const HandleGroupAccessOwnerActions: Reducer<IReportingProfileState, GroupAccessOwnerViewActions> = (state, action) => {
	const groupAccessOwnersFetchedFor = new Set(
		state.reportingProfileConfigs
			.filter((config) => GetViewIdentifier(config.profileId) === action.forView)
			.map((c) => c.reportingProfileConfigId),
	);

	// If group access owners were not fetched on behalf of any of the reporting profile config(s)
	if (groupAccessOwnersFetchedFor.size === 0) {
		return state;
	}

	switch (action.type) {
		case GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_STARTED:
		case GroupAccessOwnerViewActionTypes.REQUEST_PEOPLE:
			return {
				...state,
				fetchingGroupAccessOwners: true,
			};
		case GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_FINISHED:
			return {
				...state,
				fetchingGroupAccessOwners: false,
			};
		case GroupAccessOwnerViewActionTypes.PEOPLE_FETCHED: {
			const total = action.people.pageRange.total;

			// update any profile where the fetched amount is different from what is stored in survey builder.
			const outOfSyncReportingProfileConfigs: number[] = [];
			const reportingProfileConfigs = state.reportingProfileConfigs.map((c) => {
				const configId = c.reportingProfileConfigId;

				if (groupAccessOwnersFetchedFor.has(configId) && total && c.userCount !== total) {
					outOfSyncReportingProfileConfigs.push(configId);
					return { ...c, userCount: total };
				}

				return c;
			});

			return {
				...state,
				fetchingGroupAccessOwners: false,
				reportingProfileConfigs,
				outOfSyncReportingProfileConfigIds: outOfSyncReportingProfileConfigs,
			};
		}
		case GroupAccessOwnerViewActionTypes.COLUMN_CONSTRUCTION_FAILED:
		case GroupAccessOwnerViewActionTypes.PEOPLE_COULDNOT_BEFETCHED:
			return {
				...state,
				fetchingGroupAccessOwners: 'N/A',
			};
		default:
			return state;
	}
};
